import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Export, CaretDown, CaretRight, DownloadSimple } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { AdminShell } from "@/components/AdminShell";

const CHIP = {
  allowed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  confidential: "bg-red-50 text-red-700 border-red-200",
  new_unreviewed: "bg-amber-50 text-amber-800 border-amber-300",
};
const ICON = { allowed: "🟢", confidential: "🔴", new_unreviewed: "🆕" };

const FieldChip = ({ coll, f, onCycle }) => (
  <button data-testid={`export-field-chip-${coll}-${f.field}`}
    onClick={() => onCycle(coll, f)}
    title={f.status === "allowed" ? "Allowed in exports — click to mark confidential"
      : f.status === "confidential" ? "Confidential — stripped from exports. Click to allow"
      : "NEW field — blocked until you review it. Click to allow"}
    className={`text-[11px] font-mono border rounded-full px-2 py-0.5 transition-colors hover:opacity-75 ${CHIP[f.status]}`}>
    {ICON[f.status]} {f.field}
  </button>
);

export default function AdminExport() {
  const [colls, setColls] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [open, setOpen] = useState(new Set());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [mode, setMode] = useState("aggregated");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/export-db/schema").then((r) => setColls(r.data.collections)).catch((e) => toast.error(errDetail(e)));
  }, []);

  const toggle = (set, v, setter) => {
    const n = new Set(set);
    n.has(v) ? n.delete(v) : n.add(v);
    setter(n);
  };

  const cycle = async (coll, f) => {
    const next = f.status === "allowed" ? "confidential" : "allowed";
    try {
      await api.put("/admin/export-db/policy", { collection: coll, field: f.field, status: next });
      setColls((cs) => cs.map((c) => c.collection !== coll ? c
        : { ...c, fields: c.fields.map((x) => x.field === f.field ? { ...x, status: next } : x) }));
      toast.success(`${coll}.${f.field} → ${next === "allowed" ? "🟢 allowed" : "🔴 confidential"}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const estKb = mode === "aggregated"
    ? Math.max(2, Math.round((from && to ? 12 : 6) * 0.6))
    : Math.max(1, Math.round([...sel].reduce((s, cn) => s + (colls?.find((c) => c.collection === cn)?.count || 0), 0) * 0.22));

  const download = async () => {
    setBusy(true);
    try {
      const body = { collections: [...sel], date_from: from || null, date_to: to || null, mode };
      const res = await api.post("/admin/export-db", body, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers["content-disposition"]?.match(/filename="(.+)"/) || [])[1] || "export.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export ZIP downloaded");
    } catch (e) {
      let msg = errDetail(e);
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail; } catch {}
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const newCount = colls?.reduce((s, c) => s + c.fields.filter((f) => f.status === "new_unreviewed").length, 0) || 0;

  return (
    <AdminShell title="Export DB" subtitle="Export any slice of the database — confidential fields are stripped, new fields stay blocked until you review them. Attach the ZIP to the AI Advisor for deep-dive questions." testId="admin-export-page">
      <div data-testid="export-page" className="space-y-5">
        <div className="flex items-end gap-4 flex-wrap border border-gray-200 rounded-lg p-4 bg-gray-50">
          <label className="text-xs text-gray-600">From
            <input data-testid="export-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="block border border-gray-300 rounded-md px-2 py-1.5 text-sm mt-0.5 bg-white" />
          </label>
          <label className="text-xs text-gray-600">To
            <input data-testid="export-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="block border border-gray-300 rounded-md px-2 py-1.5 text-sm mt-0.5 bg-white" />
          </label>
          <div className="text-xs text-gray-600">Mode
            <div className="flex gap-1 mt-0.5">
              {["aggregated", "raw"].map((m) => (
                <button key={m} data-testid={`export-mode-${m}`} onClick={() => setMode(m)}
                  className={`text-sm rounded-md px-3 py-1.5 border font-semibold capitalize ${mode === m ? "bg-blue-600 text-white border-blue-600" : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>
          <span data-testid="export-size-estimate" className="text-xs text-gray-500 pb-2">Est. size: ~{estKb} KB</span>
          <button data-testid="export-download-btn" onClick={download}
            disabled={busy || (mode === "raw" && sel.size === 0)}
            className="ml-auto flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
            <DownloadSimple size={15} /> {busy ? "Building…" : "Download ZIP"}
          </button>
        </div>
        {mode === "aggregated" && (
          <p data-testid="export-agg-note" className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-4 py-2.5">
            Aggregated mode exports monthly rollups (revenue, costs, commissions, token usage, signups) + a data dictionary — no per-row data, safest to share with the AI Advisor. Collection selection is ignored.
          </p>
        )}
        {newCount > 0 && (
          <p data-testid="export-new-fields-warning" className="text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded-md px-4 py-2.5">
            🆕 {newCount} new field{newCount > 1 ? "s" : ""} discovered since your last review — blocked from exports until you click to allow.
          </p>
        )}
        {!colls ? <p className="text-sm text-gray-400 p-6" data-testid="export-loading">Scanning collections…</p> : (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="export-collection-list">
            {colls.map((c) => (
              <div key={c.collection} data-testid={`export-coll-${c.collection}`}>
                <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
                  <input data-testid={`export-coll-checkbox-${c.collection}`} type="checkbox"
                    checked={sel.has(c.collection)} disabled={mode === "aggregated"}
                    onChange={() => toggle(sel, c.collection, setSel)} className="accent-blue-600 disabled:opacity-40" />
                  <button data-testid={`export-expand-${c.collection}`} onClick={() => toggle(open, c.collection, setOpen)}
                    className="flex items-center gap-2 flex-1 text-left">
                    {open.has(c.collection) ? <CaretDown size={13} className="text-gray-400" /> : <CaretRight size={13} className="text-gray-400" />}
                    <span className="text-sm font-semibold text-gray-900 font-mono">{c.collection}</span>
                    <span className="text-xs text-gray-400">{c.count.toLocaleString()} docs · {c.fields.length} fields{c.date_field ? ` · dated by ${c.date_field}` : " · no date field"}</span>
                    {c.fields.some((f) => f.status === "new_unreviewed") && <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">🆕 NEW FIELDS</span>}
                  </button>
                </div>
                {open.has(c.collection) && (
                  <div className="px-11 pb-3 flex flex-wrap gap-1.5" data-testid={`export-fields-${c.collection}`}>
                    {c.fields.map((f) => <FieldChip key={f.field} coll={c.collection} f={f} onCycle={cycle} />)}
                    {c.fields.length === 0 && <span className="text-xs text-gray-400">Empty collection — no fields sampled</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 flex items-center gap-1.5"><Export size={13} /> Raw exports strip 🔴 confidential and 🆕 unreviewed fields, cap 5,000 rows per collection, and include a manifest + data dictionary. Attach the ZIP in Profit Estimation → "⌸ attach".</p>
      </div>
    </AdminShell>
  );
}
